import type { SerializedError } from '@reduxjs/toolkit';
import type { FetchBaseQueryError } from '@reduxjs/toolkit/query';
import useStyles from 'isomorphic-style-loader/useStyles';
import { Flame, Lightbulb } from 'lucide-react';
import { Header } from 'semantic-ui-react';

import Err from '@components/err';
import LogoLoader from '@components/logo-loader';

import { noVulnerabilityDataMessage } from '../quick-search.utils';

import VulnerabilitySearchResultList from './vulnerability-search-result-list';
import type { VulnerabilityLookupItem } from './vulnerability-search.models';
import s from './vulnerability-search-result.scss';

export type VulnerabilitySearchResultProps =
  | { loading: boolean; onComplete: () => void; status: 'searching' }
  | {
      error: FetchBaseQueryError | SerializedError | undefined;
      onRetry: () => void;
      status: 'error';
    }
  | {
      activeIndex: number;
      listboxId: string;
      onActivate: (index: number) => void;
      onSelect: () => void;
      query: string;
      status: 'results';
      vulnerabilities: VulnerabilityLookupItem[];
    }
  | { builtAt: string | null; query: string; status: 'noData' }
  | { status: 'idle' };

/**
 * Content area of the vulnerability scope: the in-flight loader, idle guidance,
 * the matched CVE list, the no-results state, or a lookup error.
 */
export const VulnerabilitySearchResult = (
  props: VulnerabilitySearchResultProps,
) => {
  useStyles(s);

  if (props.status === 'searching') {
    return (
      <div className={s.loadingPane} role="status">
        <LogoLoader
          loading={props.loading}
          message="Searching…"
          onComplete={props.onComplete}
        />
      </div>
    );
  }

  if (props.status === 'error') {
    // Unknown ids return 200 with empty results, so a 403 is the only
    // differentiated failure the lookup can produce.
    const errorType =
      props.error && 'status' in props.error && props.error.status === 403
        ? 'Forbidden'
        : 'InternalServer';

    return (
      <div className={s.status} role="alert">
        <Err
          className={s.errorPane}
          error={props.error}
          errorType={errorType}
          imageSize="small"
          onRetryButtonClick={props.onRetry}
          noBorder
        />
      </div>
    );
  }

  if (props.status === 'results') {
    return (
      <VulnerabilitySearchResultList
        activeIndex={props.activeIndex}
        listboxId={props.listboxId}
        onActivate={props.onActivate}
        onSelect={props.onSelect}
        query={props.query}
        vulnerabilities={props.vulnerabilities}
      />
    );
  }

  if (props.status === 'noData') {
    return (
      <div className={s.status} role="status">
        <img alt="" className={s.statusImage} src="/img/robot_notfound.svg" />

        <Header as="h2" className={s.statusHeading}>
          No results found
        </Header>

        <p className={s.statusMessage}>
          {noVulnerabilityDataMessage(props.query, props.builtAt)}
        </p>
      </div>
    );
  }

  return (
    <div className={s.guidance}>
      <Header as="h2" className={s.guidanceLabel}>
        What can you search for?
        <Lightbulb aria-hidden className={s.guidanceLabelIcon} />
      </Header>

      <div className={s.card}>
        <Header as="h3" className={s.cardHeader}>
          <Flame aria-hidden className={s.cardHeaderIcon} />
          <span>Application vulnerabilities</span>
        </Header>
        <div className={s.cardBody}>
          <span className={s.cardDescription}>
            Find if a vulnerability is affecting your applications.
          </span>
          <span className={s.searchBy}>
            Search by:
            <code className={s.exampleChip}>CVE-YYYY-NNNNN</code>
            or
            <code className={s.exampleChip}>GHSA-xxxx-xxxx</code>
          </span>
        </div>
      </div>
    </div>
  );
};

export default VulnerabilitySearchResult;
